import { ZonedDateTime, ZoneId } from '@js-joda/core';
import ArchiveIcon from '@mui/icons-material/Archive';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
  Tooltip,
} from '@mui/material';
import React from 'react';
import type { RaRecord } from 'react-admin';
import {
  EditButton,
  Button as ReactAdminButton,
  SaveButton,
  ShowButton,
  Toolbar,
  useCreatePath,
  useNotify,
  useRecordContext,
  useRefresh,
  useUpdate,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import useCanWrite from '../../../hooks/useCanWrite';
import useIsExerciseEditor from '../../../hooks/useIsExerciseEditor';

export const rowActionsSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 1,
  alignItems: 'center',
  whiteSpace: 'nowrap' as const,
  width: '100%',
};

export const actionsHeaderLabel = (
  <Box sx={{ width: '100%', textAlign: 'right' }}>Actions</Box>
);

type ArchiveResourceButtonProps = {
  resource: string;
};

/** Child counts from Admin*Basics fragments (list + getOne). Used only for hierarchy resources. */
function getExerciseHierarchyChildCount(
  resource: string,
  record: RaRecord,
): number {
  switch (resource) {
    case 'ExerciseTypeCategoryGroup': {
      const connection = record.exerciseTypeCategories as
        | { count?: number | null }
        | undefined;
      return Number(connection?.count ?? 0);
    }
    case 'ExerciseTypeCategory': {
      const connection = record.exerciseTypes as
        | { count?: number | null }
        | undefined;
      return Number(connection?.count ?? 0);
    }
    case 'ExerciseType': {
      const connection = record.exerciseBases as
        | { count?: number | null }
        | undefined;
      return Number(connection?.count ?? 0);
    }
    case 'ExerciseBase': {
      const connection = record.exercises as
        | { count?: number | null }
        | undefined;
      return Number(connection?.count ?? 0);
    }
    default:
      return 0;
  }
}

function archiveDisabledChildTooltip(resource: string): string {
  switch (resource) {
    case 'ExerciseTypeCategoryGroup':
      return 'Cannot archive while this group has movement type categories. Click to open that filtered list.';
    case 'ExerciseTypeCategory':
      return 'Cannot archive while this category has movement types. Click to open that filtered list.';
    case 'ExerciseType':
      return 'Cannot archive while this movement type has base movements. Click to open that filtered list.';
    case 'ExerciseBase':
      return 'Cannot archive while this base movement has movements. Click to open that filtered list.';
    default:
      return '';
  }
}

export const ArchiveResourceButton: React.FC<ArchiveResourceButtonProps> = ({
  resource,
}) => {
  const isExerciseEditor = useIsExerciseEditor();
  const canWrite = useCanWrite();
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const createPath = useCreatePath();
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = React.useState(false);
  const [update, { isPending }] = useUpdate();
  const canManageExercises = canWrite || isExerciseEditor;
  const childCount = record
    ? getExerciseHierarchyChildCount(resource, record)
    : 0;
  const archiveBlockedByChildren = childCount > 0;

  if (!record || !canManageExercises) {
    return null;
  }

  const stopRowClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleOpenConfirm = (event: React.SyntheticEvent) => {
    stopRowClick(event);
    setIsConfirmOpen(true);
  };

  const handleArchiveClick = (event: React.SyntheticEvent) => {
    stopRowClick(event);
    if (archiveBlockedByChildren && record) {
      const id = String(record.id);
      let targetResource: string;
      let filter: Record<string, string>;
      switch (resource) {
        case 'ExerciseTypeCategoryGroup':
          targetResource = 'ExerciseTypeCategory';
          filter = { exerciseTypeCategoryGroupId: id };
          break;
        case 'ExerciseTypeCategory':
          targetResource = 'ExerciseType';
          filter = { exerciseTypeCategoryId: id };
          break;
        case 'ExerciseType':
          targetResource = 'ExerciseBase';
          filter = { exerciseTypeId: id };
          break;
        case 'ExerciseBase':
          targetResource = 'Exercise';
          filter = { exerciseBaseId: id };
          break;
        default:
          return;
      }
      const basePath = createPath({ resource: targetResource, type: 'list' });
      const params = new URLSearchParams();
      params.set('filter', JSON.stringify(filter));
      navigate(`${basePath}?${params.toString()}`);
      return;
    }
    handleOpenConfirm(event);
  };

  const handleOpenRestoreConfirm = (event: React.SyntheticEvent) => {
    stopRowClick(event);
    setIsRestoreConfirmOpen(true);
  };

  const handleCloseConfirm = (event: React.SyntheticEvent) => {
    stopRowClick(event);
    setIsConfirmOpen(false);
  };

  const handleCloseRestoreConfirm = (event: React.SyntheticEvent) => {
    stopRowClick(event);
    setIsRestoreConfirmOpen(false);
  };

  const handleConfirmArchive = (event: React.SyntheticEvent) => {
    stopRowClick(event);
    update(
      resource,
      {
        id: record.id,
        data: {
          ...record,
          id: record.id,
          archivedAt: ZonedDateTime.now(ZoneId.UTC).toString(),
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          setIsConfirmOpen(false);
          notify('Archived');
          refresh();
        },
        onError: (error: Error) => {
          notify(error.message, { type: 'warning' });
        },
      },
    );
  };

  const handleConfirmRestore = (event: React.SyntheticEvent) => {
    stopRowClick(event);
    update(
      resource,
      {
        id: record.id,
        data: {
          ...record,
          id: record.id,
          archivedAt: null,
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          setIsRestoreConfirmOpen(false);
          notify('Restored');
          refresh();
        },
        onError: (error: Error) => {
          notify(error.message, { type: 'warning' });
        },
      },
    );
  };

  if (record.isArchived ?? !!record.archivedAt) {
    return (
      <>
        <ReactAdminButton
          label="Restore"
          sx={{ minWidth: 'auto' }}
          onMouseDown={stopRowClick}
          onClick={handleOpenRestoreConfirm}
          disabled={isPending}
        >
          <RestoreIcon />
        </ReactAdminButton>
        <Dialog
          open={isRestoreConfirmOpen}
          onClose={(_event) => setIsRestoreConfirmOpen(false)}
          onClick={stopRowClick}
        >
          <DialogTitle>Restore {record.name ?? record.id}</DialogTitle>
          <DialogContent>
            <DialogContentText>Are you sure?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MuiButton
              onMouseDown={stopRowClick}
              onClick={handleCloseRestoreConfirm}
              disabled={isPending}
            >
              Cancel
            </MuiButton>
            <MuiButton
              onMouseDown={stopRowClick}
              onClick={handleConfirmRestore}
              disabled={isPending}
            >
              Restore
            </MuiButton>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  const canArchive = !archiveBlockedByChildren && !isPending;

  return (
    <>
      <Tooltip
        title={canArchive ? null : archiveDisabledChildTooltip(resource)}
      >
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <ReactAdminButton
            label="Archive"
            color="error"
            onMouseDown={stopRowClick}
            onClick={handleArchiveClick}
            disabled={!canArchive}
          >
            <ArchiveIcon />
          </ReactAdminButton>
        </Box>
      </Tooltip>
      <Dialog
        open={isConfirmOpen}
        onClose={(_event) => setIsConfirmOpen(false)}
        onClick={stopRowClick}
      >
        <DialogTitle>Archive {record.name ?? record.id}</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton
            onMouseDown={stopRowClick}
            onClick={handleCloseConfirm}
            disabled={isPending}
          >
            Cancel
          </MuiButton>
          <MuiButton
            onMouseDown={stopRowClick}
            onClick={handleConfirmArchive}
            color="error"
            disabled={isPending}
          >
            Archive
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const CategoryHierarchyEditToolbar: React.FC<{
  resource: string;
}> = ({ resource }) => (
  <Toolbar sx={{ justifyContent: 'space-between' }}>
    <SaveButton />
    <ArchiveResourceButton resource={resource} />
  </Toolbar>
);

export const CategoryRowActions: React.FC<{ resource: string }> = ({
  resource,
}) => {
  const isExerciseEditor = useIsExerciseEditor();
  const canWrite = useCanWrite();
  const canManageExercises = canWrite || isExerciseEditor;

  return (
    <Box sx={rowActionsSx}>
      {canManageExercises && <EditButton />}
      <ShowButton />
      <ArchiveResourceButton resource={resource} />
    </Box>
  );
};
